/**
 * orderLabelEscPos.ts — Builder ESC/POS de la etiqueta de VENTA EN ESPERA (caja).
 *
 * QUÉ HACE:
 *  - Genera los bytes ESC/POS de la etiqueta que imprime el carrito con
 *    «Imprimir etiqueta del pedido»: el pedido se guarda AUTOMÁTICAMENTE como
 *    venta en espera (con token) y la etiqueta es su comprobante:
 *    · Detalle del pedido con la MISMA estructura que "DETALLE DE VENTA" del
 *      ticket (rejilla Cantidad | Producto | Total + SKU, P.UNIT y variantes):
 *      reutiliza el pipeline compartido `cartItemsToTicketLines` →
 *      `renderItemsTableEscPos`, sin duplicar lógica.
 *    · Código de barras CORTO y ÚNICO (CODE128 subconjunto C, `98<token 8>` =
 *      10 dígitos): cabe SIEMPRE con barra ancha (GS w ≥ 2) y legible — un
 *      solo escaneo con pistola recupera la venta en espera en el mismo equipo.
 *    · QR AUTOCONTENIDO `{"w":"<token>","p":[…]}`: lleva TODO el pedido
 *      adentro — se lee en CUALQUIER caja/dispositivo (o sin venta en
 *      espera) y no caduca. Un solo escaneo con lector 2D.
 *
 * QUÉ EXPORTA: buildOrderLabelEscPosAsync (async), OrderLabelEscPosInput.
 *
 * QUÉ NO DEBE HACER:
 *  - No conoce de storage ni de UI; solo recibe un DTO y devuelve bytes.
 *  - No calcula precios (llegan listos en el input: carrito + total).
 */
import type { CartItem } from "@/types";
import {
  sanitizeForThermal,
  getTicketMaxWidth,
  loadBusinessForReceipt,
} from "@/features/caja/services/ticketRender.service";
import {
  renderItemsTableEscPos,
  toEscPosTableItems,
} from "@/features/caja/utils/escposItemsTable";
import { cartItemsToTicketLines } from "@/features/caja/utils/ticketLineBreakdown";
import { appendTaxLinesToTicket } from "@/features/caja/utils/cartDiscountUtils";
import { wrapThermalText } from "@/features/caja/utils/escposLayout";
import {
  buildCode128EscPosBytes,
  buildQrCodeEscPos,
} from "@/lib/escPosBarcode";
import { fmtCopSymbol } from "./cajaDisplay";

const ESC = 0x1b;
const GS = 0x1d;

/**
 * Ancho utilizable del papel en dots (con margen de seguridad). El código de
 * la etiqueta es corto (10 dígitos → 92 módulos en subconjunto C), así que
 * SIEMPRE cabe con barra ancha. 58 mm → 384 dots reales; 80 mm → 576.
 */
const PAPER_DOTS: Record<58 | 80, number> = {
  58: 356,
  80: 552,
};

export interface OrderLabelEscPosInput {
  /** Carrito completo del pedido (misma fuente que el ticket "Detalle de venta"). */
  cart: CartItem[];
  /** Total del pedido en pesos. */
  total: number;
  /** Código CODE128 ya codificado (weighBarcode.encodeParkedSaleCode128). */
  code128?: string;
  /** Texto QR ya codificado (weighBarcode.encodeParkedSaleQr) — opcional. */
  qrText?: string;
  paperWidthMm?: 58 | 80;
}

const joinChunks = (chunks: Uint8Array[]): Uint8Array => {
  const total = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
};

/**
 * Tamaño de módulo del QR según la longitud del contenido: más datos = módulo
 * más pequeño (el QR grande no cabe en 58 mm con módulo 6). Módulo 2 solo
 * para QRs muy largos (pedidos con muchos items) para que quepan en 58 mm.
 */
const qrModuleSizeForLength = (len: number): number => {
  if (len > 500) return 2;
  if (len > 320) return 3;
  if (len > 150) return 4;
  return 6;
};

/**
 * Construye la etiqueta de venta en espera (comprobante de trabajo): detalle
 * del pedido en la misma estructura de tabla que el ticket, TOTAL, un solo
 * código de barras corto (token de la venta en espera) con barra ancha y el
 * QR — sin nodos DOM ocultos.
 */
export async function buildOrderLabelEscPosAsync(
  input: OrderLabelEscPosInput,
): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const paper = input.paperWidthMm === 80 ? 80 : 58;
  const maxWidth = getTicketMaxWidth(paper);

  const add = (text: string) => {
    chunks.push(encoder.encode(sanitizeForThermal(text) + "\n"));
  };
  const addRaw = (bytes: number[]) => {
    chunks.push(new Uint8Array(bytes));
  };

  // 1. Inicializar
  addRaw([ESC, 0x40]);
  addRaw([ESC, 0x61, 0x01]); // Centro

  // 2. Negocio (negrita)
  const biz = loadBusinessForReceipt();
  if (biz.nombre) {
    addRaw([ESC, 0x45, 0x01]);
    add(biz.nombre.toUpperCase());
    addRaw([ESC, 0x45, 0x00]);
  }

  // 3. Título — DOBLE ancho/alto + negrita
  addRaw([ESC, 0x21, 0x30]);
  add("VENTA EN ESPERA");
  addRaw([ESC, 0x21, 0x00]);

  // 4. Fecha corta (dd/mm hh:mm)
  const now = new Date();
  const fecha = `${now.toLocaleDateString("es-CO")} ${now.toLocaleTimeString(
    "es-CO",
    {
      hour: "2-digit",
      minute: "2-digit",
    },
  )}`;
  add(fecha);
  addRaw([ESC, 0x61, 0x00]); // Izquierda
  add("-".repeat(maxWidth));

  // 5. Detalle del pedido — MISMA estructura que "DETALLE DE VENTA": rejilla
  //    Cantidad | Producto | Total con SKU, P.UNIT, IVA (incluido) y variantes
  //    por línea (reutiliza el pipeline compartido del ticket, sin duplicar
  //    lógica). La etiqueta es interna pero se mantiene idéntica al ticket.
  const tableRows: Array<{ text: string; bold: boolean }> = [];
  let rowBold = false;
  renderItemsTableEscPos({
    items: toEscPosTableItems(
      appendTaxLinesToTicket(
        cartItemsToTicketLines(input.cart),
        input.cart,
        loadBusinessForReceipt().impuestoDefaultPct ?? 0,
      ),
    ),
    paperWidthMm: paper,
    emitLine: (s) => tableRows.push({ text: s, bold: rowBold }),
    bold: (on) => {
      rowBold = on;
    },
    money: (n) => `$${Math.round(n).toLocaleString("es-CO")}`,
    wrap: wrapThermalText,
    showColumnHeader: true,
    thermal: true,
  });

  // Volcar las filas a la térmica con negrita por fila (ESC E) y texto ASCII.
  addRaw([ESC, 0x61, 0x00]);
  let prevBold = false;
  for (const row of tableRows) {
    if (row.bold !== prevBold) {
      prevBold = row.bold;
      addRaw([ESC, 0x45, row.bold ? 0x01 : 0x00]);
    }
    add(row.text);
  }
  if (prevBold) addRaw([ESC, 0x45, 0x00]);

  // 6. Total (destacado)
  add("-".repeat(maxWidth));
  addRaw([ESC, 0x61, 0x01]);
  addRaw([ESC, 0x21, 0x10]); // Doble alto
  addRaw([ESC, 0x45, 0x01]);
  add(`TOTAL: ${fmtCopSymbol(Math.round(input.total))}`);
  addRaw([ESC, 0x45, 0x00]);
  addRaw([ESC, 0x21, 0x00]);
  addRaw([ESC, 0x61, 0x00]);

  // 7. Código de barras ÚNICO y CORTO (token de la venta en espera, 10
  //    dígitos) en subconjunto C con barra ancha (GS w ≥ 2): SIEMPRE cabe y
  //    se lee perfecto — un solo escaneo recupera todo el pedido guardado.
  if (input.code128) {
    const digits = input.code128.length;
    const modules = Math.ceil(digits / 2) * 11 + 37;
    const dots = PAPER_DOTS[paper];
    const barWidth = Math.max(2, Math.min(4, Math.floor(dots / modules)));
    addRaw([0x0a]);
    addRaw([ESC, 0x61, 0x01]);
    addRaw([ESC, 0x45, 0x01]);
    add("ESCANEA EL CODIGO EN CAJA");
    addRaw([ESC, 0x45, 0x00]);
    addRaw([ESC, 0x61, 0x01]);
    chunks.push(
      buildCode128EscPosBytes(input.code128, {
        height: 56,
        barWidth,
        subset: "C",
      }),
    );
    addRaw([ESC, 0x61, 0x00]);
    // HRI en texto: verificación manual y tecleo del código en el buscador
    // (si la pistola falla o el código quedó cortado).
    addRaw([ESC, 0x61, 0x01]);
    add(`CODIGO: ${input.code128}`);
    addRaw([ESC, 0x61, 0x00]);
    addRaw([0x0a]);
  }

  // 8. QR AUTOCONTENIDO (un solo escaneo con lector 2D recupera el pedido
  //    completo desde el código mismo — funciona en OTRA caja/dispositivo,
  //    sin venta en espera, y no caduca)
  if (input.qrText) {
    addRaw([0x0a]);
    addRaw([ESC, 0x61, 0x01]);
    add("SCANEA EL QR EN OTRO EQUIPO");
    addRaw([ESC, 0x61, 0x01]);
    chunks.push(
      buildQrCodeEscPos(input.qrText, {
        moduleSize: qrModuleSizeForLength(input.qrText.length),
      }),
    );
    addRaw([ESC, 0x61, 0x00]);
  }

  // 9. Espacio final y CORTE automático (GS V 65 n — full cut con feed)
  addRaw([0x0a, 0x0a, 0x0a]);
  addRaw([GS, 0x56, 65, 0x10]);

  return joinChunks(chunks);
}
